import type { ComponentProps, ReactNode } from "react";
import { Link } from "react-router";

import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost";
type Size = "md" | "lg";

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-full font-medium " +
  "transition-[transform,background-color,border-color,opacity] duration-300 " +
  "ease-[var(--ease-calm)] whitespace-nowrap " +
  "hover:-translate-y-0.5 active:translate-y-0 " +
  "disabled:pointer-events-none disabled:opacity-50";

const VARIANTS: Record<Variant, string> = {
  primary: cn(
    "bg-emerald-500 text-ink-950",
    "shadow-[0_0_0_1px_rgba(52,211,153,0.35),0_10px_40px_-12px_rgba(16,185,129,0.65)]",
    "hover:bg-emerald-400",
  ),
  secondary: cn(
    "border border-white/15 text-cream-50 bg-white/[0.02]",
    "hover:bg-white/[0.06] hover:border-white/25",
  ),
  ghost: "text-cream-100/80 hover:text-cream-50",
};

const SIZES: Record<Size, string> = {
  md: "h-11 px-5 text-[0.95rem]",
  lg: "h-13 px-7 text-base",
};

type CommonProps = {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: ReactNode;
};

/** Internal navigation. */
export function ButtonLink({
  to,
  variant = "primary",
  size = "md",
  className,
  children,
  ...rest
}: CommonProps & { to: string } & Omit<
    ComponentProps<typeof Link>,
    "to" | "className" | "children"
  >) {
  return (
    <Link
      to={to}
      className={cn(BASE, VARIANTS[variant], SIZES[size], className)}
      {...rest}
    >
      {children}
    </Link>
  );
}

/**
 * Navigation out of the marketing site — in practice, into the THKY platform.
 * The href always comes from `platform-url.ts`, never from a literal.
 */
export function ButtonAnchor({
  href,
  variant = "primary",
  size = "md",
  className,
  children,
  ...rest
}: CommonProps & ComponentProps<"a">) {
  return (
    <a
      href={href}
      className={cn(BASE, VARIANTS[variant], SIZES[size], className)}
      {...rest}
    >
      {children}
    </a>
  );
}

export function Button({
  variant = "primary",
  size = "md",
  className,
  children,
  ...rest
}: CommonProps & ComponentProps<"button">) {
  return (
    <button
      className={cn(BASE, VARIANTS[variant], SIZES[size], className)}
      {...rest}
    >
      {children}
    </button>
  );
}
